import {
  Table,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  Icon,
  ICONS_ENUM,
  IDirection,
  DIRECTION
} from '@sanlam-glacier/app-common/components';
import { ToDoListRow } from './ToDoListRow';
import styles from './to-do-list.module.scss';
import style from '../../../../pages/intermediary/intermediary.module.scss';
import { useEffect, useState } from 'react';

import clsx from 'clsx';
import { ToDoListTableHeader } from './ToDoListTableHeader';
import { ToDoListNoResults } from './to-do-list-no-results/ToDoListNoResults';
import { useBreakpoint, BreakpointKey } from 'providers/breakpoint/BreakpointProvider';
import {
  ToDoListTab,
  ToDoListEntry,
  NegativeJourneyCategory,
  SelectedFilterCategory,
  ToDoListTableHeaderConfig,
  ToDoListColumns,
  toDoListTableHeaderKeys
} from 'modules/dashboard/types';

type ToDoListTableProps = {
  selectedTab: ToDoListTab;
  entries: ToDoListEntry[];
  currentPage: number;
  setSelectedTabCurrentPage: (page: number) => void;
  negativeJourneyType?: NegativeJourneyCategory;
  searchTerm?: string;
  selectedFilter?: string;
  selectedFilterType?: SelectedFilterCategory;
};

export const ToDoListTable = ({
  selectedTab,
  entries,
  currentPage,
  setSelectedTabCurrentPage,
  negativeJourneyType,
  searchTerm,
  selectedFilter,
  selectedFilterType
}: ToDoListTableProps) => {
  const testComponentName = 'ToDoList';

  const breakpointContext = useBreakpoint();

  const [pageSize, setPageSize] = useState(
    getPageSizeBasedOnBreakpoint(breakpointContext.breakpoint)
  );
  const [page, setPage] = useState(currentPage);

  const [toDoListTableHeaderData, setToDoListTableHeaderData] = useState<
    ToDoListTableHeaderConfig<ToDoListColumns>[]
  >([]);

  useEffect(() => {
    const intialHeaderData: ToDoListTableHeaderConfig<ToDoListColumns>[] = [
      {
        key: toDoListTableHeaderKeys.clientName,
        label: 'Client',
        direction: DIRECTION.ASC,
        className: clsx(style.headers)
      },
      {
        key: toDoListTableHeaderKeys.investmentNumber,
        label: 'Investment number',
        direction: DIRECTION.ASC,
        className: clsx(style.headers, styles.investmentNumber)
      },
      {
        key: toDoListTableHeaderKeys.investmentName,
        label: 'Investment name',
        direction: DIRECTION.ASC,
        className: clsx(style.headers)
      },
      {
        key: toDoListTableHeaderKeys.toDoItem,
        label: 'To-do item',
        direction: DIRECTION.ASC,
        className: clsx(style.headers)
      },
      {
        key: toDoListTableHeaderKeys.dueDate,
        label: 'Due date',
        direction: DIRECTION.ASC,
        className: clsx(style.headers, styles.dueDate)
      },
      {
        key: toDoListTableHeaderKeys.status,
        label: 'Status',
        direction: selectedTab === ToDoListTab.due ? DIRECTION.DESC : DIRECTION.ASC,
        className:
          selectedTab === ToDoListTab.due
            ? clsx(style.headers, styles.dueStatus)
            : selectedTab === ToDoListTab.overdue
              ? clsx(style.headers, styles.overdueStatus)
              : clsx(style.headers, styles.notCompliantStatus)
      }
    ];
    setToDoListTableHeaderData(intialHeaderData);
  }, [selectedTab]);

  const paginatedData = entries.slice(page * pageSize, page * pageSize + pageSize);

  const toggleDirection = (direction: IDirection): IDirection => {
    return direction === DIRECTION.ASC ? DIRECTION.DESC : DIRECTION.ASC;
  };

  const handleSort = (
    property: ToDoListTableHeaderConfig<ToDoListColumns>['key'] | string,
    direction: IDirection
  ) => {
    const newDirection = toggleDirection(direction);

    setToDoListTableHeaderData((prev) =>
      prev.map((header) => {
        if (header.key === property) {
          return { ...header, direction: newDirection };
        }
        return header;
      })
    );

    switch (property) {
      case toDoListTableHeaderKeys.clientName:
        entries.sort((a, b) =>
          newDirection === DIRECTION.DESC
            ? a.clientName.localeCompare(b.clientName)
            : b.clientName.localeCompare(a.clientName)
        );
        break;
      case toDoListTableHeaderKeys.investmentNumber:
        entries.sort((a, b) =>
          newDirection === DIRECTION.DESC
            ? Number(a.investmentNumber) - Number(b.investmentNumber)
            : Number(b.investmentNumber) - Number(a.investmentNumber)
        );
        break;
      case toDoListTableHeaderKeys.investmentName:
        entries.sort((a, b) =>
          newDirection === DIRECTION.DESC
            ? a.investmentName.localeCompare(b.investmentName)
            : b.investmentName.localeCompare(a.investmentName)
        );
        break;
      case toDoListTableHeaderKeys.toDoItem:
        entries.sort((a, b) =>
          newDirection === DIRECTION.DESC
            ? a.toDoItemTitle.localeCompare(b.toDoItemTitle)
            : b.toDoItemTitle.localeCompare(a.toDoItemTitle)
        );
        break;
      case toDoListTableHeaderKeys.dueDate:
      case toDoListTableHeaderKeys.status:
        entries.sort((a, b) =>
          newDirection === DIRECTION.DESC
            ? a.toDoItemNumOfDays - b.toDoItemNumOfDays
            : b.toDoItemNumOfDays - a.toDoItemNumOfDays
        );
        break;
      default:
        break;
    }
  };

  let tableHeaderData =
    selectedTab === ToDoListTab.notCompliant
      ? toDoListTableHeaderData.filter((header) => header.key !== 'dueDate')
      : toDoListTableHeaderData;

  if (breakpointContext.breakpoint === 'sm') {
    tableHeaderData = tableHeaderData.filter(
      (header) => header.key !== 'investmentNumber' && header.key !== 'investmentName'
    );
  }

  useEffect(() => {
    setPage(currentPage);
    setPageSize(getPageSizeBasedOnBreakpoint(breakpointContext.breakpoint));
  }, [breakpointContext.breakpoint, currentPage]);

  return (
    <div className={styles.toDoListTableContainer}>
      {entries.length === 0 ? (
        <ToDoListNoResults
          negativeJourneyType={negativeJourneyType}
          searchTerm={searchTerm}
          selectedFilter={selectedFilter}
          selectedFilterType={selectedFilterType}
        />
      ) : (
        <Table className={styles.table}>
          <ToDoListTableHeader
            headers={tableHeaderData}
            align={TableCell.ALIGN.LEFT}
            tableHeaderClassName={styles.tableHead}
            orderBy='dueDate'
            handleSort={handleSort}
          />
          <TableBody>
            {paginatedData.map((row) => (
              <ToDoListRow key={row.toDoItemId} row={row} selectedTab={selectedTab} />
            ))}
          </TableBody>
        </Table>
      )}
      <div className={styles.paginationAndClientBtn}>
        {entries.length > 0 && (
          <TablePagination
            maxVisibleItems={3}
            hideRowsPerPage
            showLabels={false}
            className={styles.pagination}
            count={entries.length}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={(newPage: number) => {
              setSelectedTabCurrentPage(newPage);
              setPage(newPage);
            }}
            onRowsPerPageChange={(newPageSize: number) => {
              setPageSize(newPageSize);
            }}
            rowsPerPageOptions={undefined}
            data-testid={`${testComponentName}Pagination`}
          />
        )}

        <Button
          data-testid={`${testComponentName}GoToClientBtn`}
          variant={Button.VARIANT.SECONDARY}
          size={Button.SIZE.SMALL}
          endIcon={<Icon icon={ICONS_ENUM.NAVIGATION_ARROW_FORWARD} />}
          className={styles.goToClientsButton}
        >
          Go to clients
        </Button>
      </div>
    </div>
  );
};

const getPageSizeBasedOnBreakpoint = (breakpoint: BreakpointKey) => {
  if (breakpoint === 'md' || breakpoint === 'sm') return 4;
  return 5;
};
